/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from 'react';
import { AdminCheckbox } from '@project/components';
import { Form, Tabs, Tab, Button } from 'react-bootstrap';
import spaceProfileDefaults from '../SpaceProfile/defaults';
import formSchema from '../BasicInfoForm/schema'
import { RootContext } from '../../hoc/RootContext/RootContext';
import { get, getNestedElement } from '../../utils/utils';
import styles from './CompanySettingsTabs.module.css';
import CompanyIntegrationsTab from '../CompanyIntegrationsTab/CompanyIntegrationsTab';
import CompanyGeneralTab from '../CompanyGeneralTab/CompanyGeneralTab';
import { ASSET_SECTION_LABELS } from '../../constants/index';
import { useIntl } from 'react-intl';

const SKIP_FIELDS = ['floorplan_upload', 'virtual_tour_link', 'white_box_link'];
const SKIP_FIELDS_PHOTOS = ['client_provided_sq_ft', 'floorplan_upload', 'virtual_tour_link', 'white_box_link', 'integrations.integration_property_id', 'integrations.idp_record_id', 'basic_info.separate_rpm_renders'];

const CompanySettingsTabs = ({ selectedCompany, isSaving, onSaveCompanySettings, companySettings }) => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  let schema = formSchema.basic_info

  const [fieldSettings, setFieldSettings] = useState(null);
  const { assetSchema } = useContext(RootContext);

  const SETTINGS_LABELS = {
    visible: t('CompanySettingsTabs.visible'),
    required: t('CompanySettingsTabs.required'),
    softRequired: t('CompanySettingsTabs.softRequired'),
    enabled: t('CompanySettingsTabs.enabled'),
    surveyed: t('CompanySettingsTabs.notSurveyed'),
    photosEnabled: 'Enable Photos'
  };

  const generateFieldSettings = (settings) => {
    // This will populate the generated field settings with the stored value, or default to true
    const initBasicInfoFieldValues = (fields, objKey) => {
      Object.keys(fields).forEach(field => {
        const fieldKey = objKey ? `${objKey}.${field}` : field;
        if (field !== null && typeof fields[field] === 'object') {
          return initBasicInfoFieldValues(fields[field], fieldKey);
        }
        fields[field] = {};

        // determine the schema based default for if the field is required to publish

        // populate each field setting with either default values, or the ones contained in the saved field settings
        ['visible', 'required', 'softRequired', 'enabled', 'surveyed', 'photosEnabled'].forEach(setting => {
          const defaultValue = get(
            schema,
            fieldKey.replace('basic_info.', '') + `.${setting}`,
            ['required', 'softRequired', 'surveyed', 'photosEnabled'].includes(setting) ? false : true
          );
          // get the setting for this or use the default value defined (or assumed) from the schema
          fields[field][setting] = get(settings, `${fieldKey}.${setting}`, defaultValue);
        });
      });
    }

    // build the basic info fields into the fields data structure
    const fields = { basic_info: JSON.parse(JSON.stringify(spaceProfileDefaults.basic_info)) };
    // this will set true or false values on the fields pertaining to the company's settings
    initBasicInfoFieldValues(fields.basic_info, 'basic_info');

    // build and init asset related fields onto the field datastructure
    const savedFieldAssets = (settings && settings.assets && Array.isArray(settings.assets)) ? settings.assets : [];
    fields.assets = Object.keys(assetSchema).map(section => {
      const savedSection = savedFieldAssets.find(_ => _.section === section) || { assets: [] };
      return {
        section,
        assets: assetSchema[section].map(asset => {
          const savedAsset = savedSection.assets.find(_ => _.type === asset.type);
          return {
            type: asset.type,
            visible: savedAsset ? savedAsset.visible : true,
            fields: asset.fields.filter(_ => _.type !== 'label' && _.id !== 'coalesce_not_present_assets').map(field => {
              const savedField = savedAsset && savedAsset.fields && Array.isArray(savedAsset.fields) ? savedAsset.fields.find(_ => _.id === field.id) : null;
              return { id: field.id, visible: savedField ? savedField.visible : true }
            })
          }
        })
      }
    });

    return fields;
  }

  // rebuild the fieldSettings object anytime that the company is changed
  useEffect(() => {
    setFieldSettings(generateFieldSettings(companySettings.fieldSettings));
  }, [companySettings]);

  // handles the action of toggling a field on or off, and updating the field data strucuture accordingly
  const toggleFieldVisibility = (e, fieldBase, key) => {
    fieldBase[key] = e.target.checked;
    setFieldSettings({ ...fieldSettings });
  }

  // build the toggles for the basic info fields
  const buildBasicFields = (fields, fieldList = [], objKey) => {
    Object.keys(fields).forEach(field => {
      const fieldKey = objKey ? `${objKey}.${field}` : field;
      const titleize = (value) => value.replace(/_/g, ' ').replace(/\b[a-z](?=[a-z]{2})/g, (_) => _.toUpperCase());

      if (field !== null && typeof fields[field] === 'object' && fields[field].required === undefined) {
        const translatedFieldLabel = getNestedElement(schema, fieldKey.split('.').splice(1).join('.')).label;
        fieldList.push({ type: 'section', node: <div className="font-lg font-weight-bold mt-4">{translatedFieldLabel || titleize(field)}</div> });
        return buildBasicFields(fields[field], fieldList, fieldKey);
      };

      let photosSetting = [];

      if (SKIP_FIELDS.includes(field)) return; // This field is not intended to be configurable
      if (!SKIP_FIELDS_PHOTOS.includes(field)) photosSetting = ['photosEnabled'];

      const translatedFieldLabel = getNestedElement(schema, fieldKey.split('.').splice(1).join('.')).label;
      const fieldLabel = translatedFieldLabel || (schema[fieldKey] ? schema[fieldKey].label : titleize(field));

      const node = (
        <div className={`${styles.basicInfoNode} mb-2`} key={fieldKey}>
          <label className="font-weight-bold">{fieldLabel}</label>
          {['visible', 'required', 'softRequired', 'enabled', 'surveyed', ...photosSetting].map(setting =>
            <AdminCheckbox
              key={`${fieldKey}_${setting}`}
              id={`${fieldKey}_${setting}`}
              label={SETTINGS_LABELS[setting] || setting}
              checked={!!fields[field][setting]}
              onChange={(e) => toggleFieldVisibility(e, fields[field], setting)} />
          )}
        </div>
      );

      fieldList.push({ type: 'input', node })
    });
    return fieldList;

  }

  // builds the field toggles for the assets and related fields
  const buildAssetFields = (fields, fieldList = []) => {
    const titleize = (value) => value.replace(/_/g, ' ').replace(/\b[a-z](?=[a-z]{2})/g, (_) => _.toUpperCase());

    fields.forEach(({ section, assets }) => {
      const sectionLabel = ASSET_SECTION_LABELS[titleize(section)];

      fieldList.push({ type: 'section', node: <h2 className='is-main-heading'>{sectionLabel || titleize(section)}</h2> });
      assets.forEach(asset => {
        const assetTypeSchema = assetSchema[section].find(_ => _.type === asset.type)
        fieldList.push({ type: 'section', node: <h3 className='mt-4 font-weight-bold'>{(assetTypeSchema && assetTypeSchema.displayName) || titleize(asset.type)}</h3> });
        fieldList.push({
          type: 'input',
          node: <AdminCheckbox id={`${asset.type}_${section}_main`} label={intl.formatMessage({ id: 'CompanySettingsTabs.showAssets' }, { type: (assetTypeSchema && assetTypeSchema.displayName) || titleize(asset.type) })} checked={!!asset.visible} onChange={(e) => toggleFieldVisibility(e, asset, 'visible')} />
        });

        asset.fields.forEach((field, i) => {
          if (field.id.indexOf('ada-') === 0) return;
          const assetSchema = assetTypeSchema.fields.find(_ => _.id === field.id);

          fieldList.push({
            type: 'input',
            node: <AdminCheckbox id={`${asset.type}_${field.id}_${i}`} label={(assetSchema && assetSchema.name) || titleize(field.id)} checked={!!field.visible} onChange={(e) => toggleFieldVisibility(e, field, 'visible')} />
          })
        })
      })
    });

    return fieldList;
  }

  return (
    <Tabs id="company-settings-tabs" variant="pills" className="font-lg">
      <Tab eventKey="general" title={t('CompanySettingsTabs.title')}>
        <CompanyGeneralTab
          isSaving={isSaving}
          company={selectedCompany}
          onSaveCompanySettings={onSaveCompanySettings}
          companySettings={companySettings} />
      </Tab>
      <Tab eventKey="field_visibility" title={t('CompanySettingsTabs.fieldSettings.tabTitle')}>
        <Form key="field_visibility_form" className="is-common-form">
          <div className='row my-sm-5'>
            <div className="font-lg col-sm-8" style={{ lineHeight: '40px' }}>
              {t('CompanySettingsTabs.fieldSettings.description')}
            </div>
            <div className="col-sm-4" align="right"><Button disabled={isSaving} onClick={() => onSaveCompanySettings({ fieldSettings })} >
              {t('CompanySettingsTabs.fieldSettings.saveButton')}
            </Button></div>
          </div>
          <h2 className='is-main-heading'>{t('CompanySettingsTabs.fieldSettings.basicInfoTitle')}</h2>
          <div className='row my-sm-5'>
            {fieldSettings && buildBasicFields(fieldSettings.basic_info, undefined, 'basic_info').map((field, i) => (
              <div key={i} className={`col-sm-${field.type === 'input' ? '6' : '12'}`}>
                {field.node}
              </div>
            ))}
            {fieldSettings && buildAssetFields(fieldSettings.assets, undefined, 'assets').map((field, i) => (
              <div key={i} className={`col-sm-${field.type === 'input' ? '6' : '12'}`}>
                {field.node}
              </div>
            ))}
          </div>
        </Form>
      </Tab>
      <Tab eventKey="integrations" title={t('CompanySettingsTabs.integrations.tabTitle')}>
        <CompanyIntegrationsTab
          isSaving={isSaving}
          company={selectedCompany}
          onSaveCompanySettings={onSaveCompanySettings}
          companySettings={companySettings} />
      </Tab>
    </Tabs>
  )
};

export default CompanySettingsTabs;